import { useState } from 'react';
import {
  Card,
  CardHeader,
  Badge,
  Button,
  Input,
  LoadingSkeleton,
  StatCard,
  StatsGrid,
  ProgressBar,
} from '@nexora/design-system';
import { useFiscalDashboard, type FiscalDeadline } from './useFiscalDashboard';
import { formatCurrency, formatDateTime, formatCount, EMPTY } from '../../../shared/utils/format';

/* ───────────────────────────────────────────────────────────────────────────
   Portado de base-html, slug `erpone_painel_de_apura_o_e_conformidade_fiscal`
   → Painel de Apuração e Conformidade Fiscal — GET /fiscal/dashboard.

   Da referência vieram: os quatro indicadores no topo (Total Tax Accrued,
   Receita Bruta, Alíquota Efetiva, Cálculos do Motor), a "Tax Burden
   Breakdown" por tributo, os "Upcoming Deadlines" com status e as "Recent
   Transmissions".

   Não vieram (não existem no backend — seriam números inventados):
   * "Compliance Score" — não há endpoint de conformidade;
   * "Tax Savings (Opt)" — não há otimização calculada;
   * "Pending Liabilities" como lista separada — o backend devolve somente
     `upcomingDeadlines` (obrigações com vencimento).

   A competência é um input YYYY-MM (FiscalDashboardQueryDto). O tenant vem da
   sessão; `useAuth` não é necessário aqui porque o contrato não tem
   tenantId — o AuthGuard injeta `req.tenantId` no servidor.
   ─────────────────────────────────────────────────────────────────────────── */

const DEADLINE_VARIANT: Record<FiscalDeadline['status'], 'success' | 'warning' | 'error' | 'info'> = {
  OK: 'success',
  WARNING: 'warning',
  OVERDUE: 'error',
};

const DEADLINE_LABEL: Record<FiscalDeadline['status'], string> = {
  OK: 'Em dia',
  WARNING: 'Atenção',
  OVERDUE: 'Vencida',
};

/** Percentual que o backend já envia como número (18 → "18,0%"). */
function formatPct(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return EMPTY;
  return `${Number(v).toFixed(1).replace('.', ',')}%`;
}

/** Data de vencimento: "15 out" quando no mês corrente. */
function deadlineDue(d: FiscalDeadline): string {
  const dt = new Date(d.dueDate);
  if (Number.isNaN(dt.getTime())) return EMPTY;
  return dt.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

export function FiscalDashboardPage(): JSX.Element {
  const hoje = new Date();
  const competenciaAtual = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
  const [competencia, setCompetencia] = useState(competenciaAtual);

  const { data, isLoading, error, refetch } = useFiscalDashboard({ competencia });

  const burden = data?.taxBurden;
  const deadlines = data?.upcomingDeadlines ?? [];
  const transmissions = data?.recentTransmissions ?? [];

  const warnings = deadlines.filter((d) => d.status === 'WARNING').length;
  const overdue = deadlines.filter((d) => d.status === 'OVERDUE').length;

  return (
    <div className="fiscal-dashboard-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Painel de Apuração e Conformidade Fiscal
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Carga tributária do período, obrigações com vencimento e transmissões recentes.
          </p>
        </div>
        <div className="fiscal-dashboard__period">
          <Input
            label="Competência"
            type="month"
            value={competencia}
            onChange={(e) => setCompetencia(e.target.value)}
            inputSize="sm"
          />
        </div>
      </div>

      {/* KPIs */}
      {isLoading ? (
        <div className="fiscal-dashboard__loading">
          <LoadingSkeleton count={4} height="96px" borderRadius="12px" />
        </div>
      ) : (
        <StatsGrid columns={4}>
          <StatCard
            label="Total de tributos apurados"
            value={burden ? formatCurrency(burden.totalTaxes) : EMPTY}
            icon={<span className="material-symbols-outlined" aria-hidden="true">receipt_long</span>}
            variant="primary"
          />
          <StatCard
            label="Receita bruta do período"
            value={burden ? formatCurrency(burden.grossRevenue) : EMPTY}
            icon={<span className="material-symbols-outlined" aria-hidden="true">trending_up</span>}
            variant="success"
          />
          <StatCard
            label="Alíquota efetiva"
            value={burden ? formatPct(burden.effectiveRatePct) : EMPTY}
            icon={<span className="material-symbols-outlined" aria-hidden="true">percent</span>}
            variant="info"
          />
          <StatCard
            label="Cálculos do motor fiscal"
            value={data?.auditStats ? formatCount(data.auditStats.totalCalculations) : EMPTY}
            icon={<span className="material-symbols-outlined" aria-hidden="true">calculate</span>}
            variant="warning"
            trend={
              data?.auditStats?.lastCalculationAt
                ? { value: `último: ${formatDateTime(data.auditStats.lastCalculationAt)}`, direction: 'neutral' }
                : undefined
            }
          />
        </StatsGrid>
      )}

      {error && !isLoading && (
        <Card className="fiscal-dashboard__error">
          <p className="m-0" style={{ fontWeight: 600, color: '#ba1a1a' }}>Erro ao carregar o painel</p>
          <p className="m-0" style={{ fontSize: '0.875rem', color: '#74777f' }}>
            {error instanceof Error ? error.message : 'Erro desconhecido'}
          </p>
          <Button variant="primary" onClick={() => refetch()}>Tentar novamente</Button>
        </Card>
      )}

      {!isLoading && !error && (
        <div className="fiscal-dashboard__grid">
          {/* Tax Burden Breakdown */}
          <Card variant="outlined" padding="0">
            <CardHeader className="px-md pt-md" action={
              <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
                {burden?.breakdown.length ?? 0} {((burden?.breakdown.length ?? 0) === 1 ? 'tributo' : 'tributos')}
              </span>
            }>
              <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Carga tributária por tributo</h3>
            </CardHeader>
            {(burden?.breakdown.length ?? 0) === 0 ? (
              <div className="fiscal-dashboard__empty">
                <p className="m-0">Nenhum tributo apurado no período — sem documentos autorizados ou todos com valor zero.</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="fiscal-table">
                  <thead>
                    <tr>
                      <th>Tributo</th>
                      <th align="right">Base (R$)</th>
                      <th align="right">Valor (R$)</th>
                      <th align="right">Participação</th>
                      <th align="right">Docs.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {burden?.breakdown.map((line) => (
                      <tr key={line.taxType}>
                        <td>
                          <strong>{line.label}</strong>
                          <small className="cell-sub">{line.taxType}</small>
                        </td>
                        <td align="right" className="tabular-nums">{formatCurrency(line.base)}</td>
                        <td align="right" className="tabular-nums">{formatCurrency(line.value)}</td>
                        <td className="fiscal-dashboard__share">
                          <ProgressBar value={line.sharePct} max={100} size="sm" className="fiscal-dashboard__bar" />
                          <span className="tabular-nums">{formatPct(line.sharePct)}</span>
                        </td>
                        <td align="right" className="tabular-nums">{formatCount(line.documentCount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Upcoming Deadlines */}
          <Card variant="outlined" padding="0">
            <CardHeader className="px-md pt-md" action={
              <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
                {warnings > 0 || overdue > 0 ? `${warnings + overdue} precisam de atenção` : 'tudo em dia'}
              </span>
            }>
              <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Obrigações com vencimento</h3>
            </CardHeader>
            {deadlines.length === 0 ? (
              <div className="fiscal-dashboard__empty">
                <p className="m-0">Nenhuma obrigação com vencimento no período.</p>
              </div>
            ) : (
              <ul className="fiscal-deadlines">
                {deadlines.map((d) => (
                  <li key={d.id} className="fiscal-deadlines__item">
                    <div className="fiscal-deadlines__main">
                      <strong>{d.title}</strong>
                      <small>{d.description}</small>
                    </div>
                    <div className="fiscal-deadlines__meta">
                      <span className="fiscal-deadlines__date">
                        {deadlineDue(d)}
                        <small>{d.daysLeft < 0 ? `${Math.abs(d.daysLeft)}d atraso` : `${d.daysLeft}d`}</small>
                      </span>
                      <Badge variant={DEADLINE_VARIANT[d.status]} size="sm">{DEADLINE_LABEL[d.status]}</Badge>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}

      {/* Recent Transmissions */}
      {!isLoading && !error && (
        <Card variant="outlined" padding="0">
          <CardHeader className="px-md pt-md" action={
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {transmissions.length} transmissões
            </span>
          }>
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Transmissões recentes</h3>
          </CardHeader>
          {transmissions.length === 0 ? (
            <div className="fiscal-dashboard__empty">
              <p className="m-0">Nenhuma transmissão registrada ainda.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="fiscal-table">
                <thead>
                  <tr>
                    <th>Evento</th>
                    <th>Data/Hora</th>
                    <th>Status</th>
                    <th>Provedor</th>
                    <th>UF/SEFAZ</th>
                    <th>Protocolo</th>
                  </tr>
                </thead>
                <tbody>
                  {transmissions.map((t) => (
                    <tr key={t.id}>
                      <td><strong>{t.evento}</strong></td>
                      <td className="tabular-nums">{formatDateTime(t.dataHora)}</td>
                      <td><Badge size="sm" variant={t.status === 'SUCCESS' || t.status === 'AUTHORIZED' ? 'success' : t.status === 'ERROR' || t.status === 'REJECTED' ? 'error' : 'info'}>{t.status}</Badge></td>
                      <td>{t.providerType}</td>
                      <td>{t.sefazState ?? '—'}</td>
                      <td className="tabular-nums">{t.authorizationProtocol ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <style>{`
        .fiscal-dashboard-page { padding: 1.5rem; }
        .fiscal-dashboard__period { min-width: 180px; }
        .fiscal-dashboard__loading { display: grid; gap: 1rem; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); }
        .fiscal-dashboard__error { padding: 2rem; text-align: center; color: #74777f; display: grid; gap: 0.5rem; justify-items: center; }
        .fiscal-dashboard__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1rem; }
        .fiscal-dashboard__empty { padding: 2rem; text-align: center; color: #74777f; font-size: 0.875rem; }
        .fiscal-dashboard__share { display: flex; align-items: center; gap: 0.5rem; justify-content: flex-end; }
        .fiscal-dashboard__bar { flex: 1; max-width: 120px; }
        .fiscal-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .fiscal-table th {
          text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e;
          font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em;
          border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;
        }
        .fiscal-table td { padding: 0.625rem 1rem; border-bottom: 1px solid #eceef0; vertical-align: middle; }
        .fiscal-table tbody tr:hover { background: #f8fafc; }
        .cell-sub { display: block; color: #74777f; font-size: 0.75rem; }
        .tabular-nums { font-variant-numeric: tabular-nums; }
        .fiscal-deadlines { list-style: none; margin: 0; padding: 0; }
        .fiscal-deadlines__item { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; padding: 0.625rem 1rem; border-bottom: 1px solid #eceef0; }
        .fiscal-deadlines__item:last-child { border-bottom: none; }
        .fiscal-deadlines__main { display: flex; flex-direction: column; }
        .fiscal-deadlines__main small { color: #74777f; font-size: 0.75rem; }
        .fiscal-deadlines__meta { display: flex; align-items: center; gap: 0.75rem; }
        .fiscal-deadlines__date { display: flex; flex-direction: column; align-items: flex-end; font-size: 0.875rem; font-weight: 600; }
        .fiscal-deadlines__date small { color: #74777f; font-weight: 400; }
        @media (max-width: 1024px) { .fiscal-dashboard__grid { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
}